import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as pdfjsLib from 'pdfjs-dist';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';

const WORKER_SOURCES = [
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js',
  'https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js'
];
pdfjsLib.GlobalWorkerOptions.workerSrc = WORKER_SOURCES[0];

const apiUrl = process.env.REACT_APP_API_URL;

function AdminAssignmentSubmissionPdf() {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const canvasRef = useRef(null);
  const pdfDocumentRef = useRef(null);
  const { submissionId, filename, assignmentTitle, studentName } = location.state || {};

  const getToken = () => JSON.parse(sessionStorage.getItem('adminSession'))?.token;

  useEffect(() => {
    let mounted = true;
    const initWorker = async () => {
      for (const src of WORKER_SOURCES) {
        try {
          pdfjsLib.GlobalWorkerOptions.workerSrc = src;
          await new Promise((resolve) => setTimeout(resolve, 300));
          if (mounted) setLoading(false);
          return;
        } catch (err) { /* try next source */ }
      }
      if (mounted) { setError('Failed to initialize PDF viewer.'); setLoading(false); }
    };
    initWorker();
    return () => { mounted = false; };
  }, []);

  const loadPdf = async () => {
    try {
      setError('');
      const token = getToken();
      if (!token) throw new Error('No authentication token available. Please log in again.');
      const response = await axios.get(`${apiUrl}/api/admin/assignments/submissions/${submissionId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'arraybuffer',
      });
      const pdf = await pdfjsLib.getDocument({ data: response.data }).promise;
      pdfDocumentRef.current = pdf;
      setPageCount(pdf.numPages);
      if (canvasRef.current) renderPage(1, pdf);
    } catch (err) {
      console.error('Error loading submission PDF:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load the submitted PDF.');
    }
  };

  const renderPage = async (pageNumber, pdf = pdfDocumentRef.current) => {
    try {
      const canvas = canvasRef.current;
      if (!pdf || !canvas) return;
      const page = await pdf.getPage(pageNumber);
      const viewport = page.getViewport({ scale });
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      await page.render({ canvasContext: context, viewport }).promise;
    } catch (err) {
      setError('Failed to render page: ' + err.message);
    }
  };

  useEffect(() => {
    if (!loading && !error && submissionId) loadPdf();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, submissionId]);

  useEffect(() => {
    if (pdfDocumentRef.current && canvasRef.current) renderPage(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, scale]);

  const handlePrevPage = () => currentPage > 1 && setCurrentPage(currentPage - 1);
  const handleNextPage = () => currentPage < pageCount && setCurrentPage(currentPage + 1);
  const handleZoomIn = () => setScale((prev) => Math.min(prev + 0.25, 3.0));
  const handleZoomOut = () => setScale((prev) => Math.max(prev - 0.25, 0.5));
  const handleGoBack = () => navigate(-1);

  const handleDownload = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${apiUrl}/api/admin/assignments/submissions/${submissionId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || 'submission.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Error downloading file.');
    }
  };

  if (!submissionId) {
    return (
      <>
        <SideBar />
        <div className="dashboard-main-wrapper">
          <TopNavBar />
          <div className="dashboard-body">
            <div className="alert alert-danger m-4">
              <h4>Error</h4>
              <p>Submission information not found. Please go back and try again.</p>
              <button onClick={handleGoBack} className="btn btn-primary">Go Back</button>
            </div>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li><Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">Home</Link></li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><Link to="/admin-assignments" className="text-gray-200 fw-normal text-15 hover-text-main-600">Assignments</Link></li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><span className="text-main-600 fw-normal text-15">View Submission</span></li>
              </ul>
            </div>
            <button onClick={handleGoBack} className="btn btn-secondary">
              <i className="ph ph-arrow-left me-2"></i> Back
            </button>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="d-flex justify-content-between align-items-center flex-wrap">
                <div>
                  <h4 className="mb-0">{studentName || 'Submission'}</h4>
                  {assignmentTitle && <p className="text-muted mb-0">{assignmentTitle}</p>}
                </div>
                <div className="d-flex align-items-center gap-2">
                  {!loading && !error && pageCount > 0 && (
                    <span className="badge bg-success">Page {currentPage} of {pageCount}</span>
                  )}
                  <div className="btn-group">
                    <button onClick={handleZoomOut} className="btn btn-sm btn-outline-secondary" disabled={loading || error || scale <= 0.5} title="Zoom Out">
                      <i className="ph ph-magnifying-glass-minus"></i>
                    </button>
                    <button className="btn btn-sm btn-outline-secondary" disabled>{Math.round(scale * 100)}%</button>
                    <button onClick={handleZoomIn} className="btn btn-sm btn-outline-secondary" disabled={loading || error || scale >= 3.0} title="Zoom In">
                      <i className="ph ph-magnifying-glass-plus"></i>
                    </button>
                  </div>
                  <button onClick={handleDownload} className="btn btn-sm btn-info" title="Download">
                    <i className="ph ph-download-simple"></i>
                  </button>
                </div>
              </div>
            </div>

            <div
              className="card-body p-0 text-center"
              style={{ height: '70vh', overflow: 'auto', backgroundColor: '#f5f5f5', display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}
            >
              {error && (
                <div className="alert alert-danger m-4 w-100">
                  <h5>Error Loading PDF</h5>
                  <p>{error}</p>
                  <div className="mt-3">
                    <button onClick={handleDownload} className="btn btn-info me-2">
                      <i className="ph ph-download-simple me-2"></i> Download Instead
                    </button>
                    <button onClick={handleGoBack} className="btn btn-secondary">
                      <i className="ph ph-arrow-left me-2"></i> Go Back
                    </button>
                  </div>
                </div>
              )}

              {loading && !error && (
                <div className="text-center p-5 w-100">
                  <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                    <span className="visually-hidden">Loading PDF...</span>
                  </div>
                  <p className="mt-3">Loading PDF viewer...</p>
                </div>
              )}

              {!loading && !error && (
                <div style={{ padding: '20px', minWidth: '100%' }}>
                  <canvas ref={canvasRef} style={{ maxWidth: '100%', height: 'auto', boxShadow: '0 4px 8px rgba(0,0,0,0.1)', display: 'block', margin: '0 auto' }} />
                </div>
              )}
            </div>

            <div className="card-footer">
              <div className="d-flex justify-content-between align-items-center flex-wrap">
                <small className="text-muted"><i className="ph ph-file-pdf me-1"></i>{filename}</small>
                {!loading && !error && pageCount > 0 && (
                  <div className="d-flex gap-2">
                    <button onClick={handlePrevPage} className="btn btn-sm btn-primary" disabled={currentPage <= 1}>
                      <i className="ph ph-caret-left me-1"></i> Previous
                    </button>
                    <span className="btn btn-sm btn-outline-secondary disabled">{currentPage} / {pageCount}</span>
                    <button onClick={handleNextPage} className="btn btn-sm btn-primary" disabled={currentPage >= pageCount}>
                      Next <i className="ph ph-caret-right ms-1"></i>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminAssignmentSubmissionPdf;
